import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orderKeys } from '@/services/queryKeys';
import { orderService, splitOrderResult } from '@/services/order.service';
import type { ConfirmOrderVariables, CreateOrderReceiptBody } from '@/services/order.service';

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => orderService.cancel(id, reason),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useConfirmOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirm_outstanding_debt }: ConfirmOrderVariables) =>
      orderService.confirm(id, { confirm_outstanding_debt }),
    onSuccess: (_, variables) => {
      const { id } = variables;
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(id) });
    },
  });
}

export function useShipOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tracking_number, carrier }: { id: string; tracking_number: string; carrier: string }) =>
      orderService.ship(id, tracking_number, carrier),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useCompleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => orderService.complete(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(id) });
    },
  });
}

export function useRetryOffPlatformAccounting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => orderService.retryOffPlatformAccounting(orderId),
    onSuccess: (_, orderId) => {
      qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
    },
  });
}

/** Retry the Document Scenario Engine run (CTBH/PXK/HĐĐT nháp) for an off-platform order. */
export function useRetryOffPlatformPackedScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => orderService.retryOffPlatformPackedScenario(orderId),
    onSuccess: (_, orderId) => {
      qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
    },
  });
}

export function useSkipOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => orderService.skip(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(id) });
    },
  });
}

export function useUnskipOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => orderService.unskip(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(id) });
    },
  });
}

export function useUpdateOrderNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => orderService.update(id, { notes: note }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderShippingNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => orderService.update(id, { shipping: { note } }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, branch_id, branch_name: _branch_name, branch_address: _branch_address }: { id: string; branch_id: string; branch_name?: string; branch_address?: string }) =>
      orderService.update(id, { store_id: branch_id }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, staff_id, staff_name: _staff_name }: { id: string; staff_id: string; staff_name?: string }) =>
      orderService.update(id, { sales_person_id: staff_id }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderWarehouseStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, item_sku, staff_id }: { id: string; item_sku: string; staff_id: string }) =>
      orderService.update(id, { items: [{ sku: item_sku, warehouse_staff_id: staff_id }] }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, customer_id }: { id: string; customer_id: string }) =>
      orderService.updateCustomer(id, customer_id),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderShipping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shipping }: { id: string; shipping: { name: string; phone: string; address: string; province?: string; citizen_id?: string; delivery_days?: number; expected_delivery_date?: string; delivery_method?: string; shipping_mode?: 'self' | 'carrier'; email?: string; ward?: string; branch_id?: string } }) => {
      const payload = {
        store_id: shipping.branch_id || undefined,
        shipping: {
          address: {
            name: shipping.name,
            phone: shipping.phone,
            address: shipping.address,
            city_id: shipping.province,
            ward_id: shipping.ward,
            email: shipping.email,
            citizen_id: shipping.citizen_id,
          },
          method: shipping.delivery_method,
          // Phải gửi kèm: `self` và `carrier` cùng có `method: 'delivery'`, bỏ
          // field này thì đơn "Giao cho ĐVVC" lưu xong tụt về tự vận chuyển.
          shipping_mode: shipping.shipping_mode,
          estimated_days: shipping.delivery_days,
          estimated_delivery: shipping.expected_delivery_date ? { from: shipping.expected_delivery_date, to: shipping.expected_delivery_date } : undefined,
        }
      };
      return orderService.update(id, payload);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, invoice }: { id: string; invoice: { name?: string; tax_id?: string; address?: string; email?: string; buyer_name?: string; buyer_cccd?: string } }) =>
      orderService.update(id, { buyer_invoice_info: invoice }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: Array<{ sku: string; qty: number; price: number; price_method?: string; warehouse_name?: string; serial?: string }> }) =>
      orderService.update(id, { items }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useSplitOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      lines,
    }: {
      id: string;
      lines: Array<{ line_id: string; quantity: number }>;
    }) => splitOrderResult(await orderService.split(id, { lines })),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useCreateOrderReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, receipt }: { id: string; receipt: CreateOrderReceiptBody }) =>
      orderService.createReceipt(id, receipt),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}


/**
 * TCR-1207 — xoá (huỷ) phiếu thu của đơn. Invalidate cả list vì `paid_amount`/trạng thái
 * thanh toán của đơn đổi theo, giống `useCreateOrderReceipt`.
 */
export function useDeleteOrderReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, voucherId, reason }: { id: string; voucherId: string; reason?: string }) =>
      orderService.deleteReceipt(id, voucherId, reason),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useRevealOrderSensitive() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      orderService.revealSensitive(id, password),
  });
}
